import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN, REFRESH_TOKEN, USER_INFO } from "constants/AuthConstant";
import { notification } from "antd";
import store from "../store";
import { signOutSuccess } from "store/slices/authSlice";
import { resetWsAuth, closeWS, updateWsToken } from "../wsClient";

// Create axios instance
const apiCall = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Refresh the access token
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    // Use plain axios to avoid interceptor loops
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken, user } = response.data;

    // Save new tokens
    localStorage.setItem(AUTH_TOKEN, accessToken);
    if (newRefreshToken) {
      localStorage.setItem(REFRESH_TOKEN, newRefreshToken);
    }
    if (user) {
      localStorage.setItem(USER_INFO, JSON.stringify(user));
    }

    // Update WebSocket with new token so it can reconnect
    updateWsToken(accessToken);

    return accessToken;
  } catch (error) {
    // Refresh failed - clear everything and close WS
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(USER_INFO);
    closeWS();
    throw error;
  }
};

// Logout helper
const handleLogout = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem(USER_INFO);
  closeWS();
  store.dispatch(signOutSuccess());
};

/**
 * Request Interceptor
 * Injects Authorization header unless skipAuth = true
 */
apiCall.interceptors.request.use(
  (config) => {
    // Skip auth header if requested
    if (config.skipAuth) {
      return config;
    }

    // SSR-safe check
    const token =
      typeof window !== "undefined" ? localStorage.getItem(AUTH_TOKEN) : null;

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor
 * Handles token refresh on 401 errors
 * Returns response.data for backward compatibility
 */
apiCall.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh logic for requests that don't need auth (like login)
    if (originalRequest?.skipAuth) {
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      // Check if it might be a token issue causing the network error
      const token = localStorage.getItem(AUTH_TOKEN);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);
      if (token && refreshToken && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshAccessToken();
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return apiCall(originalRequest);
        } catch (refreshError) {
          handleLogout();
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }

    const status = error.response?.status;

    // Handle 401 - Token expired or invalid
    if (status === 401 && !originalRequest._retry) {
      // Check if it's a token expired error (backend should send this code)
      const isTokenExpired =
        error.response?.data?.code === "TOKEN_EXPIRED" ||
        error.response?.data?.message?.toLowerCase().includes("expired") ||
        error.response?.data?.message?.toLowerCase().includes("invalid token");

      // Only try to refresh if we have a refresh token
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);
      
      if (refreshToken && (isTokenExpired || error.response?.data?.code !== "INVALID_CREDENTIALS")) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return apiCall(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);

          // Retry original request with new token
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return apiCall(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);

          // Refresh failed - logout user (but don't show notification on portal or login page)
          const isPortalRoute = window.location.pathname.startsWith('/portal');
          const isLoginPage = window.location.pathname === '/' || 
                             window.location.pathname === '/login' ||
                             window.location.pathname.includes('/auth/');
          if (!isPortalRoute && !isLoginPage) {
            notification.error({
              message: "Session Expired",
              description: "Please login again",
            });
          }
          handleLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Invalid credentials during login - don't try to refresh
      return Promise.reject(error);
    }

    // Handle 403 - Forbidden
    if (status === 403) {
      notification.error({
        message: "Access Denied",
        description:
          error.response?.data?.message ||
          "You do not have permission to perform this action",
      });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export { refreshAccessToken };
export default apiCall;
